import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styles from './Button.module.scss';

type Variant = 'primary' | 'secondary' | 'link' | 'ghost';
type Size = 'md' | 'lg';

interface BaseProps {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  children: ReactNode;
  className?: string;
}

type ButtonProps = BaseProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined;
    to?: undefined;
  };

interface AnchorProps extends BaseProps {
  href: string;
  to?: undefined;
  target?: string;
  rel?: string;
  onClick?: () => void;
  'aria-label'?: string;
}

interface LinkProps extends BaseProps {
  to: string;
  href?: undefined;
  onClick?: () => void;
  'aria-label'?: string;
}

type Props = ButtonProps | AnchorProps | LinkProps;

function classes(
  variant: Variant,
  size: Size,
  fullWidth: boolean,
  extra: string | undefined,
): string {
  return [styles.btn, styles[variant], styles[size], fullWidth ? styles.fullWidth : '', extra ?? '']
    .filter(Boolean)
    .join(' ');
}

export function Button(props: Props): JSX.Element {
  const { variant = 'primary', size = 'md', fullWidth = false, className, children } = props;
  const cls = classes(variant, size, fullWidth, className);

  if ('to' in props && props.to !== undefined) {
    const { to, onClick } = props;
    return (
      <Link to={to} className={cls} onClick={onClick} aria-label={props['aria-label']}>
        {children}
      </Link>
    );
  }

  if ('href' in props && props.href !== undefined) {
    const { href, target, rel, onClick } = props;
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        className={cls}
        onClick={onClick}
        aria-label={props['aria-label']}
      >
        {children}
      </a>
    );
  }

  const buttonProps = props as ButtonProps;
  return (
    <button
      type={buttonProps.type ?? 'button'}
      onClick={buttonProps.onClick}
      disabled={buttonProps.disabled}
      className={cls}
      aria-label={buttonProps['aria-label']}
      form={buttonProps.form}
      name={buttonProps.name}
      value={buttonProps.value}
    >
      {children}
    </button>
  );
}
